import { Link, useNavigate } from '@tanstack/react-router';
import { Gavel } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Alert, AlertDescription, AlertTitle } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';

import { displayTimeZone, useStore } from '@/shared/store/store-context';
import { useStoreEditable } from '@/shared/store/use-store-editable';

import { useActiveAuctionFor } from '../hooks/use-auctions';

import { AuctionFormDialog } from './auction-form-dialog';

export interface StoreProductAuctionPanelProps {
  storeProductId: string;
  title: string;
}

/**
 * On a store product: "In auction until …" with a link while it is a scheduled or live lot (the
 * product is then withdrawn from the cart), otherwise a "Sell as auction" button that opens the
 * create dialog with this product as the lot.
 */
export function StoreProductAuctionPanel({ storeProductId, title }: StoreProductAuctionPanelProps) {
  const { t } = useTranslation('auctions');
  const format = useFormat();
  const store = useStore();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const { editable } = useStoreEditable({ permission: 'auctions.write' });
  const [open, setOpen] = useState(false);
  const readable = can('auctions.read');
  const active = useActiveAuctionFor(store.id, storeProductId, readable);
  if (!readable || active.isPending) return null;

  if (active.data) {
    const auction = active.data;
    return (
      <Alert role="status" data-tour="store-catalog.product.auction">
        <Gavel aria-hidden />
        <AlertTitle>
          {auction.status === 'live'
            ? t('product.liveUntil', { when: format.dateTime(auction.endsAt) })
            : t('product.scheduledFrom', { when: format.dateTime(auction.startsAt) })}
        </AlertTitle>
        <AlertDescription className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span>{t('product.withdrawn')}</span>
          <Link
            to="/s/$storeId/auctions/$auctionId"
            params={{ storeId: store.id, auctionId: auction.id }}
            className="text-foreground font-medium underline-offset-4 hover:underline"
          >
            {t('product.open')}
          </Link>
        </AlertDescription>
      </Alert>
    );
  }

  if (!editable) return null;
  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-4">
        <div className="flex min-w-0 items-start gap-3">
          <Gavel aria-hidden className="text-muted-foreground mt-0.5 size-5 shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-medium">{t('product.sellTitle')}</p>
            <p className="text-muted-foreground text-sm">{t('product.sellHint')}</p>
          </div>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={() => setOpen(true)}
          data-tour="store-catalog.product.sell-as-auction"
        >
          <Gavel aria-hidden />
          {t('product.sell')}
        </Button>
      </div>
      <AuctionFormDialog
        storeId={store.id}
        currency={store.defaultCurrency}
        timeZone={displayTimeZone(store.timezone)}
        open={open}
        onOpenChange={setOpen}
        lot={{ storeProductId, title }}
        onSaved={(auction) =>
          void navigate({
            to: '/s/$storeId/auctions/$auctionId',
            params: { storeId: store.id, auctionId: auction.id },
          })
        }
      />
    </>
  );
}
